import React from 'react'

import { Fragment, useState, useCallback, useEffect } from 'react'

import { DateTime } from 'luxon'

import { v4 as uniqid } from 'uuid'

import Loader from '@Pure/Loader'

import ModelPicker from '@/Form/V2/ModelPicker'
import SubmitForm  from '@/Form/V2/SubmitForm'
import Radio       from '@/Form/V2/Radio'

import BillRows from '@/BillRows'

import getFormStatus from '@/Form/Functions/getFormStatus'

import DatePicker from '@/Form/V2/DatePicker'

import removeStatusMsg from '@function/removeStatusMsg'

import FormsStore from '@Store/Forms'

const FORM_NAME = 'Bill';

const FORM_INIT =
{
	type: 'EC',
	date: DateTime.now().toISODate()
};

export default function BillForm ({ ...props })
{
	const [submit, setSubmit] = useState();
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState({ });

	const type = FormsStore.useState(s => s[FORM_NAME]?.type);

	const [user_picker_filter, setUserPickerFilter] = useState();

	const picked_partner = FormsStore.useState(s => s[FORM_NAME]?.partner_id || '');
	const picked_user    = FormsStore.useState(s => s[FORM_NAME]?.user_id || '');
	const picked_EC      = FormsStore.useState(s => s[FORM_NAME]?.EC_id || '');

	useEffect(function ()
	{
		if (!picked_EC) return setUserPickerFilter();

		setUserPickerFilter({ EC_id: picked_EC });
	},
	[picked_EC]);

	useEffect(function ()
	{
		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { ...FORM_INIT };
		});
	},
	[]);

	const sendForm = useCallback(function (e)
	{
		if (submitting) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME]._submit_id = uniqid();
		});

		setSubmitting(true);
		setSubmit(uniqid());
	},
	[submitting]);

	const setType = useCallback(function (e)
	{
		const type = e?.target?.value;

		if (!type) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { ...FORM_INIT, type: type };
		});

		setStatus({ });
	},
	[]);

	const onError = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);
	},
	[]);

	const onSuccess = useCallback(function (update)
	{
		setSubmit(undefined);
		setStatus(update);
		setSubmitting(false);

		FormsStore.update(function (store)
		{
			store[FORM_NAME] = { ...FORM_INIT };
		});
	},
	[]);

	const show_rows = type === 'member' && picked_user
	? true
	: type === 'EC' && picked_EC
	? true
	: type === 'partner' && picked_partner
	? true
	: false;

	useEffect(function ()
	{
		if (show_rows) setStatus({ });
	},
	[show_rows]);

	const removeFieldError = useCallback(function (name)
	{
		setStatus(status => removeStatusMsg(status, name));
	},
	[]);

	const rows_area = show_rows ?
	(
		<div className="u-full-width u-positioned" style={ { minHeight: 200 } }>
			<BillRows form={ FORM_NAME }
				status={ status } mode="bill"
			/>
			{
				status.fields?.rows?.error
				? getFormStatus({ error: status.fields.rows.error })
				: undefined
			}
		</div>
	)
	: undefined;

	return (
		<>

			<div className="c-form c-form--bill">

				<h2 className="c-title c-title--form">
					Facture
				</h2>

				<div className="c-form__content-fields">

					<div className="u-full-width u-flex u-flex-wrap"
						style={ { gap: 30 } }>

						<Radio label="Facture pour un CSE"
							name="type"
							value="EC"
							checked={ type === 'EC' }
							onChange={ setType }
						/>

						<Radio label="Facture pour un membre ou un élu"
							name="type"
							value="member"
							checked={ type === 'member' }
							onChange={ setType }
						/>

						<Radio label="Facture pour un partenaire"
							name="type"
							value="partner"
							checked={ type === 'partner' }
							onChange={ setType }
						/>

					</div>

					{
						type === 'partner' ?
						(
							<ModelPicker name="partner_id" key="partner_id"
								label="Nom du partenaire"
								model="partner"
								error={ status.fields?.['partner_id']?.error }
								bemit={ ['50%'] }
								form={ FORM_NAME }
								url="/autocomplete/partners"
							/>
						)
						:
						(
							<ModelPicker name="EC_id" key="EC_id"
								label={
									type === 'member'
									? 'CSE du membre' : 'Nom du CSE'
								}
								model="enterprisecomitee"
								error={ status.fields?.['EC_id']?.error }
								bemit={ ['50%'] }
								form={ FORM_NAME }
								url="/autocomplete/ECs"
							/>
						)
					}

					{
						type === 'member' && picked_EC ?
						(
							<ModelPicker name="user_id" key="user_id"
								label="Membre de CSE (Nom/prénom)"
								model="user"
								error={ status.fields?.['user_id']?.error }
								bemit={ ['50%'] }
								form={ FORM_NAME }
								url="/autocomplete/users"
								custom={ user_picker_filter }
							/>
						)
						: undefined
					}

					{
						show_rows ?
						(
							<DatePicker name="date"
								label="Date d'émission de la facture"
								form={ FORM_NAME }
								error={ status.fields?.['date']?.error }
								removeError={ removeFieldError }
							/>
						)
						: undefined
					}

				</div>

				{ rows_area }

			</div>

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						key={ props.type }
						onError={ onError }
						onSuccess={ onSuccess }
						url={
							'/bills/' +
							(props.type === 'create' ? 'create' : 'update')
						}
					/>
				)
				: undefined
			}

			{ getFormStatus(status) }

			<button type="button" className="c-btn c-btn--extra c-btn--green
				u-margin-top-30"
				onClick={ sendForm }
				disabled={ submitting }>
				Valider et créer cette facture
				{
					submitting ?
					(
						<Loader bemit={ ['bg-green', 'white'] }
							className="c-loader"
						/>
					)
					: undefined
				}
			</button>

		</>
	);
}